/**
 * Application state store using Zustand.
 *
 * The store is composed from one slice per domain (see ./slices). Each slice is
 * a StateCreator typed over the whole AppStore, so `set` and `get` remain
 * store-wide: an action can still read or write another domain's state where
 * they genuinely interact — loading a project resets the tree, converting a
 * triage item calls into the tree's addNode, and so on. Splitting the file did
 * not partition the state, only the source.
 *
 * Adding state: put it in the slice that owns it, add it to that slice's Pick,
 * and declare it on AppStore in ./storeTypes.
 */
import { create } from 'zustand';
import type { AppStore } from './storeTypes';
import { createPreferencesSlice } from './slices/preferencesSlice';
import { createSessionSlice } from './slices/sessionSlice';
import { createProjectSlice } from './slices/projectSlice';
import { createTreeSlice } from './slices/treeSlice';
import { createInsightsSlice } from './slices/insightsSlice';
import { createTriageSlice } from './slices/triageSlice';
import { createPlanBoardSlice } from './slices/planBoardSlice';

export type { AppStore, NewNodePayload } from './storeTypes';

export const useStore = create<AppStore>((...a) => ({
    ...createPreferencesSlice(...a),
    ...createSessionSlice(...a),
    ...createProjectSlice(...a),
    ...createTreeSlice(...a),
    ...createInsightsSlice(...a),
    ...createTriageSlice(...a),
    ...createPlanBoardSlice(...a),
}));
